import React, { useState, useEffect, Suspense, lazy } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    Check,
    Rocket,
    Cloud,
    ShieldCheck,
    Sparkles,
    Cpu,
    Atom,
    Bot,
    Blocks,
    Layers,
    ChevronDown,
} from "lucide-react";
import { SectionHeading } from "../components/shared/SectionHeading";
import CTABanner from "../components/shared/CTABanner";
import RelatedCasesAndCTA from "../components/services/RelatedCasesAndCTA";
import CategoryOverviewCard from "../components/services/CategoryOverviewCard";
import { SERVICE_CATEGORIES } from "../lib/data";
import { trackEvent, EVENTS } from "../lib/analytics";
import { cn } from "../lib/utils";

const FloatingKnot = lazy(() => import("../components/three/FloatingKnot"));

const ICONS = { Rocket, Cloud, ShieldCheck, Sparkles, Cpu, Atom, Bot, Blocks, Layers };

export default function Services() {
    const [searchParams, setSearchParams] = useSearchParams();
    const initialCategoryId =
        SERVICE_CATEGORIES.find((c) => c.id === searchParams.get("category"))?.id ||
        SERVICE_CATEGORIES[0].id;

    const [activeId, setActiveId] = useState(initialCategoryId);
    const initialCategory =
        SERVICE_CATEGORIES.find((c) => c.id === initialCategoryId) || SERVICE_CATEGORIES[0];
    const [openOffering, setOpenOffering] = useState(initialCategory.offerings[0].id);

    // Sync activeId when URL param changes (e.g., Home deep-link click)
    useEffect(() => {
        const urlCat = searchParams.get("category");
        if (urlCat && urlCat !== activeId) {
            const cat = SERVICE_CATEGORIES.find((c) => c.id === urlCat);
            if (cat) {
                setActiveId(cat.id);
                setOpenOffering(cat.offerings[0]?.id);
                // Scroll to category section so deep-link lands in context
                window.setTimeout(() => {
                    const el = document.getElementById("services-category-detail");
                    el?.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 80);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const active =
        SERVICE_CATEGORIES.find((c) => c.id === activeId) || SERVICE_CATEGORIES[0];
    const ActiveIcon = ICONS[active.icon] || Sparkles;

    const onPickCategory = (id) => {
        const cat = SERVICE_CATEGORIES.find((c) => c.id === id);
        setActiveId(id);
        setOpenOffering(cat?.offerings[0]?.id);
        // Keep URL in sync (deep-link friendly)
        const next = new URLSearchParams(searchParams);
        next.set("category", id);
        setSearchParams(next, { replace: true });
    };

    return (
        <main data-testid="page-services" className="relative pt-32 md:pt-40">
            {/* HERO */}
            <section className="relative pb-14 overflow-hidden">
                <div className="absolute inset-0 -z-10 grid-lines opacity-40" />
                <div className="absolute inset-0 -z-10 bg-radial-fade" />
                <div className="absolute right-0 top-0 -z-10 h-[380px] w-[380px] hidden md:block opacity-60">
                    <Suspense fallback={null}>
                        <FloatingKnot color="#7FD03A" accent="#1E88E5" speed={0.18} />
                    </Suspense>
                </div>
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10">
                    <SectionHeading
                        eyebrow="Service Offerings"
                        title={<>Deep expertise. <span className="text-gradient-brand">One seamless team.</span></>}
                        subtitle="From AI-first transformation to quantum readiness and Web3 our service portfolio is engineered for the decade ahead."
                    />
                </div>
            </section>

            {/* CATEGORY TABS */}
            <section
                data-testid="services-category-tabs"
                className="relative py-6 sticky top-[72px] z-30 bg-ink-950/85 backdrop-blur-xl border-y border-white/5"
            >
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10">
                    <div className="flex gap-2 overflow-x-auto edge-fade-x pb-1">
                        {SERVICE_CATEGORIES.map((c) => {
                            const Icon = ICONS[c.icon] || Sparkles;
                            const isActive = c.id === activeId;
                            return (
                                <button
                                    key={c.id}
                                    type="button"
                                    onClick={() => onPickCategory(c.id)}
                                    data-testid={`service-tab-${c.id}`}
                                    className={cn(
                                        "group relative shrink-0 inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-[13px] font-medium transition-all",
                                        isActive
                                            ? "bg-brand-linear text-white border-transparent shadow-glow-brand"
                                            : "glass border-white/10 text-zinc-300 hover:border-white/30"
                                    )}
                                >
                                    <Icon className="h-4 w-4" />
                                    <span className="font-mono text-[10.5px] opacity-70">{c.num}</span>
                                    <span>{c.name}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ACTIVE CATEGORY DETAIL */}
            <section
                id="services-category-detail"
                data-testid={`service-category-${active.id}`}
                className="relative py-20 md:py-24 scroll-mt-[152px]"
            >
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-4 lg:sticky lg:top-44 self-start">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={active.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.35 }}
                            >
                                <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-linear shadow-glow-brand">
                                    <ActiveIcon className="h-6 w-6 text-white" />
                                </div>
                                <div className="mt-5 font-mono text-xs text-zinc-500">
                                    Practice · {active.num}
                                </div>
                                <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold tracking-tighter leading-[1.02]">
                                    {active.name}
                                </h2>
                                <p className="mt-5 text-zinc-400 text-[15.5px] leading-relaxed max-w-md">
                                    {active.tagline}
                                </p>
                                <div className="mt-6 text-[11px] uppercase tracking-[0.22em] text-brand-green">
                                    {active.offerings.length} sub-practices
                                </div>
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    <div className="lg:col-span-8 space-y-4">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={active.id}
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.4 }}
                                className="space-y-4"
                            >
                                {active.offerings.map((o, i) => {
                                    const isOpen = openOffering === o.id;
                                    return (
                                        <motion.article
                                            key={o.id}
                                            layout
                                            className={cn(
                                                "group glass rounded-3xl border overflow-hidden brand-border-hover",
                                                isOpen
                                                    ? "border-brand-blue/40 shadow-glow-blue"
                                                    : "border-white/5"
                                            )}
                                            data-testid={`service-offering-${o.id}`}
                                        >
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const willOpen = !isOpen;
                                                    setOpenOffering(willOpen ? o.id : null);
                                                    if (willOpen) {
                                                        trackEvent(EVENTS.SERVICE_OFFERING_EXPANDED, {
                                                            offering_id: o.id,
                                                            category_id: active.id,
                                                        });
                                                    }
                                                }}
                                                data-testid={`service-offering-toggle-${o.id}`}
                                                className="w-full flex items-start md:items-center justify-between gap-5 p-6 md:p-7 text-left"
                                            >
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-3 font-mono text-[11px] text-zinc-500">
                                                        <span className="h-px w-8 bg-gradient-to-r from-brand-blue to-brand-green" />
                                                        {active.num}.{String.fromCharCode(65 + i)}
                                                    </div>
                                                    <h3 className="mt-2.5 font-display text-xl md:text-2xl font-semibold tracking-tight leading-tight">
                                                        {o.title}
                                                    </h3>
                                                    {!isOpen && (
                                                        <p className="mt-3 text-[14px] text-zinc-400 leading-relaxed line-clamp-2 max-w-2xl">
                                                            {o.desc}
                                                        </p>
                                                    )}
                                                </div>
                                                <ChevronDown
                                                    className={cn(
                                                        "h-5 w-5 text-zinc-400 transition-transform duration-300 shrink-0",
                                                        isOpen && "rotate-180 text-brand-green"
                                                    )}
                                                />
                                            </button>

                                            <AnimatePresence initial={false}>
                                                {isOpen && (
                                                    <motion.div
                                                        initial={{ height: 0, opacity: 0 }}
                                                        animate={{ height: "auto", opacity: 1 }}
                                                        exit={{ height: 0, opacity: 0 }}
                                                        transition={{ duration: 0.35 }}
                                                        className="overflow-hidden"
                                                    >
                                                        <div className="px-6 md:px-7 pb-7 pt-0">
                                                            <p className="text-[15px] text-zinc-300 leading-relaxed max-w-3xl">
                                                                {o.desc}
                                                            </p>
                                                            <div className="mt-7 grid grid-cols-1 md:grid-cols-2 gap-6">
                                                                <div>
                                                                    <div className="text-[11px] uppercase tracking-[0.22em] text-brand-green mb-4">
                                                                        Our offerings
                                                                    </div>
                                                                    <ul className="space-y-2.5">
                                                                        {o.offers.map((x) => (
                                                                            <li
                                                                                key={x}
                                                                                className="flex items-start gap-2.5 text-[13.5px] text-zinc-300"
                                                                            >
                                                                                <span className="mt-2 h-1 w-1 rounded-full bg-brand-blue shrink-0" />
                                                                                {x}
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                                <div>
                                                                    <div className="text-[11px] uppercase tracking-[0.22em] text-brand-green mb-4">
                                                                        Client benefits
                                                                    </div>
                                                                    <ul className="space-y-2.5">
                                                                        {o.benefits.map((x) => (
                                                                            <li
                                                                                key={x}
                                                                                className="flex items-start gap-2.5 text-[13.5px] text-zinc-300"
                                                                            >
                                                                                <Check className="h-3.5 w-3.5 text-brand-green mt-[3px] shrink-0" />
                                                                                {x}
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            </div>

                                                            {/* Related case studies + Contact lead CTA */}
                                                            <RelatedCasesAndCTA offering={o} category={active} />
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </motion.article>
                                    );
                                })}
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>
            </section>

            {/* ALL CATEGORIES OVERVIEW */}
            <section
                data-testid="services-all-overview"
                className="relative py-16 md:py-20 border-t border-white/5"
            >
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10">
                    <SectionHeading
                        eyebrow="The full portfolio"
                        title="Deep expertise. One seamless team."
                        subtitle="Explore every service area each backed by a senior practice head and proven delivery playbooks."
                    />
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {SERVICE_CATEGORIES.map((c, i) => (
                            <CategoryOverviewCard
                                key={c.id}
                                category={c}
                                index={i}
                                icons={ICONS}
                                onPick={onPickCategory}
                            />
                        ))}
                    </div>
                </div>
            </section>

            <section className="relative py-16 md:py-20">
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10">
                    <CTABanner
                        eyebrow="Engage our practice leads"
                        title="Pick a service. Meet a senior lead in 48 hours."
                        subtitle="Every engagement is led by an accountable practice head not handed off."
                    />
                </div>
            </section>
        </main>
    );
}
